import React, { useState, useMemo } from 'react';
import { Crown, Check, Star, CreditCard, AlertCircle, Loader, ExternalLink, Zap, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../context/LanguageContext';

interface SubscriptionPlan {
  id: 'free' | 'monthly' | 'yearly';
  name: string;
  price: number;
  billing: string;
  features: string[];
  popular?: boolean;
  savings?: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}

const SubscriptionTab: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { select, locale } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);

  const plans: SubscriptionPlan[] = useMemo(() => {
    const isArtist = user?.user_type === 'artist';

    if (isArtist) {
      return [
        {
          id: 'free',
          name: select({ es: 'Gratuito', en: 'Free' }),
          price: 0,
          billing: select({ es: 'Siempre gratis', en: 'Always free' }),
          icon: Star,
          color: 'text-gray-600',
          bgColor: 'from-gray-100 to-gray-200',
          features: [
            select({ es: 'Sube tus obras', en: 'Upload your artworks' }),
            select({ es: 'Perfil básico', en: 'Basic profile' }),
            select({ es: 'Explora arte', en: 'Explore artwork' }),
            select({ es: 'Comentarios y likes', en: 'Comments and likes' }),
            select({ es: 'Sigue a artistas', en: 'Follow artists' }),
          ],
        },
        {
          id: 'monthly',
          name: select({ es: 'Premium para Artistas', en: 'Premium for Artists' }),
          price: 19.99,
          billing: select({ es: 'mes', en: 'mo' }),
          popular: true,
          icon: Zap,
          color: 'text-purple-600',
          bgColor: 'from-purple-500 to-pink-500',
          features: [
            select({ es: 'Exposición destacada en la plataforma', en: 'Featured exposure on the platform' }),
            select({ es: 'Análisis detallado de audiencia', en: 'Detailed audience analytics' }),
            select({ es: 'Soporte prioritario', en: 'Priority support' }),
            select({ es: 'Acceso ilimitado a todo el contenido exclusivo de artistas', en: "Unlimited access to all artists' exclusive content" }),
            select({ es: 'Descarga de obras en alta resolución', en: 'High-res artwork downloads' }),
            select({ es: 'Acceso anticipado a lanzamientos', en: 'Early access to releases' }),
            select({ es: 'Experiencia sin publicidad', en: 'Ad-free experience' }),
          ],
        },
        {
          id: 'yearly',
          name: select({ es: 'Premium Pro Artista', en: 'Artist Premium Pro' }),
          price: 159.99,
          billing: select({ es: 'Anual', en: 'Yearly' }),
          savings: select({ es: 'Ahorra €32', en: 'Save €32' }),
          icon: Crown,
          color: 'text-yellow-600',
          bgColor: 'from-yellow-400 to-orange-500',
          features: [
            select({ es: 'Todo lo de Premium', en: 'Everything in Premium' }),
            select({ es: '2 meses gratis', en: '2 months free' }),
            select({ es: 'Acceso prioritario a nuevas funciones', en: 'Priority access to new features' }),
            select({ es: 'Sesiones personalizadas para revisar y mejorar tu actividad', en: 'Personalized sessions to review and improve your activity' }),
            select({ es: 'Insignia especial por ser de los primeros en unirte', en: 'Special badge for being among the first to join' }),
          ],
        },
      ];
    }

    // Visitor / Default Plans
    return [
      {
        id: 'free',
        name: select({ es: 'Gratuito', en: 'Free' }),
        price: 0,
        billing: select({ es: 'Siempre gratis', en: 'Always free' }),
        icon: Star,
        color: 'text-gray-600',
        bgColor: 'from-gray-100 to-gray-200',
        features: [
          select({ es: 'Explora arte ilimitado', en: 'Unlimited art exploration' }),
          select({ es: 'Crea colecciones', en: 'Create collections' }),
          select({ es: 'Sigue a artistas', en: 'Follow artists' }),
          select({ es: 'Comentarios y likes', en: 'Comments and likes' }),
          select({ es: 'Perfil de usuario', en: 'User profile' }),
        ],
      },
      {
        id: 'monthly',
        name: select({ es: 'Premium para Usuarios', en: 'Premium for Users' }),
        price: 9.99,
        billing: select({ es: 'mes', en: 'mo' }),
        popular: true,
        icon: Zap,
        color: 'text-purple-600',
        bgColor: 'from-purple-500 to-pink-500',
        features: [
          select({ es: 'Exposición destacada en la plataforma', en: 'Featured exposure on the platform' }),
          select({ es: 'Análisis detallado de audiencia', en: 'Detailed audience analytics' }),
          select({ es: 'Soporte prioritario', en: 'Priority support' }),
          select({ es: 'Acceso ilimitado a todo el contenido exclusivo de artistas', en: "Unlimited access to all artists' exclusive content" }),
          select({ es: 'Descarga de obras en alta resolución', en: 'High-res artwork downloads' }),
          select({ es: 'Acceso anticipado a lanzamientos', en: 'Early access to releases' }),
          select({ es: 'Experiencia sin publicidad', en: 'Ad-free experience' }),
        ],
      },
      {
        id: 'yearly',
        name: select({ es: 'Premium Pro Usuario', en: 'User Premium Pro' }),
        price: 79.99,
        billing: select({ es: 'Anual', en: 'Yearly' }),
        savings: select({ es: 'Ahorra €16', en: 'Save €16' }),
        icon: Crown,
        color: 'text-yellow-600',
        bgColor: 'from-yellow-400 to-orange-500',
        features: [
          select({ es: 'Todo lo de Premium', en: 'Everything in Premium' }),
          select({ es: '2 meses gratis', en: '2 months free' }),
          select({ es: 'Insignia de soporte a las artes', en: 'Art supporter badge' }),
        ],
      },
    ];
  }, [select, user]);

  const getCurrentPlan = () => {
    if (!user) return plans[0];
    return plans.find(plan => plan.id === user.subscription_plan) || plans[0];
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleUpgrade = async (planId: 'monthly' | 'yearly') => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const expiresAt = new Date();
      if (planId === 'monthly') {
        expiresAt.setMonth(expiresAt.getMonth() + 1);
      } else {
        expiresAt.setFullYear(expiresAt.getFullYear() + 1);
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          subscription_plan: planId,
          subscription_status: 'active',
          subscription_started_at: new Date().toISOString(),
          subscription_expires_at: expiresAt.toISOString(),
        })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      setSuccess(select({ es: '¡Plan actualizado exitosamente!', en: 'Plan updated successfully!' }));
      
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      setError(
        select({ es: 'Error al actualizar el plan: ', en: 'Error updating the plan: ' }) +
          (err instanceof Error ? err.message : select({ es: 'Error desconocido', en: 'Unknown error' }))
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          subscription_status: 'cancelled',
        })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      setSuccess(select({ es: 'Suscripción cancelada. Mantendrás acceso premium hasta el final del período.', en: 'Subscription cancelled. You keep premium access until the end of the period.' }));
      setShowCancelModal(false);
      
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      setError(
        select({ es: 'Error al cancelar la suscripción: ', en: 'Error cancelling the subscription: ' }) +
          (err instanceof Error ? err.message : select({ es: 'Error desconocido', en: 'Unknown error' }))
      );
    } finally {
      setLoading(false);
    }
  };

  const currentPlan = getCurrentPlan();
  const isCurrentPlan = (planId: string) => planId === user?.subscription_plan;
  const isPremium = user?.subscription_plan !== 'free' && user?.subscription_status === 'active';
  const isArtist = user?.user_type === 'artist';

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
          {select({ es: 'Plan de suscripción', en: 'Subscription plan' })}
        </h3>
        <p className="text-gray-600 text-lg">
          {select({ es: 'Desbloquea todo el potencial de SoArtistic', en: 'Unlock the full potential of SoArtistic' })}
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="text-red-500 mr-3" size={20} />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
          <div className="flex items-center">
            <Check className="text-green-500 mr-3" size={20} />
            <p className="text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Current Plan Status */}
      <div className="relative overflow-hidden rounded-2xl">
        <div className={`bg-gradient-to-r ${currentPlan.bgColor} p-8`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <currentPlan.icon className="text-white" size={32} />
              </div>
              
              <div className="text-white">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-2xl font-bold">{currentPlan.name}</h3>
                  {isPremium && (
                    <span className="px-3 py-1 bg-white bg-opacity-20 backdrop-blur-sm text-white text-sm font-bold rounded-full">
                      ✨ {select({ es: 'ACTIVO', en: 'ACTIVE' })}
                    </span>
                  )}
                </div>
                
                <p className="text-white text-opacity-90 text-lg mb-2">
                  {currentPlan.price === 0
                    ? select({ es: 'Plan gratuito', en: 'Free plan' })
                    : `${formatPrice(currentPlan.price)}/${currentPlan.billing}`}
                </p>
                
                <p className="text-white text-opacity-85 text-sm mb-1">
                  {select({ es: 'Estado de la suscripción:', en: 'Subscription status:' })}{' '}
                  <span className="font-semibold">
                    {user?.subscription_status === 'active' &&
                      select({ es: 'Activa', en: 'Active' })}
                    {user?.subscription_status === 'cancelled' &&
                      select({ es: 'Cancelada', en: 'Cancelled' })}
                    {user?.subscription_status === 'expired' &&
                      select({ es: 'Expirada', en: 'Expired' })}
                    {user?.subscription_status === 'trial' &&
                      select({ es: 'En periodo de prueba', en: 'In trial period' })}
                    {!user?.subscription_status &&
                      select({ es: 'Sin suscripción', en: 'No subscription' })}
                  </span>
                </p>
                
                {user?.subscription_expires_at && (
                  <p className="text-white text-opacity-75">
                    {user.subscription_status === 'active'
                      ? select({ es: 'Renovación:', en: 'Renews on:' })
                      : select({ es: 'Activo hasta:', en: 'Active until:' })}{' '}
                    {formatDate(user.subscription_expires_at)}
                  </p>
                )}
                
                <div className="mt-4 text-white text-opacity-85 text-sm space-y-1">
                  <p>
                    <span className="font-semibold">
                      {select({ es: 'Qué se activa:', en: 'What is activated:' })}{' '}
                    </span>
                    {select({
                      es: 'Todas las ventajas premium indicadas en este plan.',
                      en: 'All premium benefits listed in this plan.',
                    })}
                  </p>
                  <p>
                    <span className="font-semibold">
                      {select({ es: 'Cuándo se activa:', en: 'When it activates:' })}{' '}
                    </span>
                    {select({
                      es: 'En el momento de confirmar la suscripción.',
                      en: 'At the moment you confirm the subscription.',
                    })}
                  </p>
                  <p>
                    <span className="font-semibold">
                      {select({ es: 'Dónde ver el estado:', en: 'Where to see the status:' })}{' '}
                    </span>
                    {select({
                      es: 'En esta pantalla, en la tarjeta de tu plan, donde se muestra el estado y la fecha de renovación.',
                      en: 'On this screen, in your plan card, where the status and renewal date are shown.',
                    })}
                  </p>
                </div>
              </div>
            </div>

            {isPremium && user?.subscription_status === 'active' && (
              <button
                onClick={() => setShowCancelModal(true)}
                className="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm text-white border border-white border-opacity-30 rounded-xl hover:bg-opacity-30 transition-all font-medium"
              >
                {select({ es: 'Cancelar plan', en: 'Cancel plan' })}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Plans */}
      <div className="space-y-6">
        {plans.map((plan) => {
          const Icon = plan.icon;
          const isCurrent = isCurrentPlan(plan.id);
          
          return (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl border-2 transition-all duration-300 ${
                plan.popular
                  ? 'border-purple-300 shadow-purple-100 shadow-xl scale-[1.02]'
                  : isCurrent
                  ? 'border-green-300 shadow-green-100 shadow-lg'
                  : 'border-gray-200 shadow-md hover:shadow-lg hover:border-gray-300'
              }`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-8">
                  <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg flex items-center gap-2">
                    <Sparkles size={14} />
                    {select({ es: 'MÁS POPULAR', en: 'MOST POPULAR' })}
                  </div>
                </div>
              )}

              {/* Savings Badge */}
              {plan.savings && (
                <div className="absolute -top-3 right-8">
                  <div className="bg-green-500 text-white px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                    {plan.savings}
                  </div>
                </div>
              )}

              <div className="p-8">
                <div className="flex flex-col gap-8">
                  {/* Left Section - Plan Info & Button */}
                  <div className="flex flex-col justify-center space-y-4 min-w-0 lg:min-w-[320px]">
                    <div className={`w-16 h-16 bg-gradient-to-r ${plan.bgColor} rounded-2xl flex items-center justify-center shadow-lg`}>
                      <Icon className="text-white" size={28} />
                    </div>
                    
                    <div className="space-y-2">
                      <h3 className="text-2xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                      <div className="flex items-baseline gap-2">
                        {plan.price === 0 ? (
                          <span className="text-3xl font-bold text-gray-900">{select({ es: 'Gratis', en: 'Free' })}</span>
                        ) : plan.id === 'yearly' && (plan.price === 159.99 || plan.price === 79.99) ? (
                          <div className="flex flex-col">
                            <div className="flex items-baseline gap-2">
                              <span className="text-3xl font-bold text-gray-900">
                                {formatPrice(plan.price === 159.99 ? 15.99 : 7.99)}
                              </span>
                              <span className="text-gray-600">/{select({ es: 'mes', en: 'mo' })}</span>
                            </div>
                            <span className="text-gray-500 text-sm font-normal">
                              {select({ es: `se cobra anualmente ${formatPrice(plan.price)}`, en: `billed annually ${formatPrice(plan.price)}` })}
                            </span>
                          </div>
                        ) : (
                          <>
                            <span className="text-3xl font-bold text-gray-900">
                              {formatPrice(plan.price)}
                            </span>
                            <span className="text-gray-600">/{plan.billing}</span>
                            {plan.id === 'yearly' && (
                              <span className="text-green-600 font-semibold ml-2">
                                (≈ {formatPrice(plan.price / 12)}/{select({ es: 'mes', en: 'mo' })})
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {isArtist && (
                      <div className="text-xs text-gray-500 leading-snug break-words">
                        {select({
                          es: '* En caso de finalizar la compra online desde SoArtistic, se cobra una comisión (incluida en los términos generales).',
                          en: '* If you make an online sale through SoArtistic, a commission is charged (included in the general terms).',
                        })}
                      </div>
                    )}
                  </div>

                  {/* Center Section - Features (Expanded) */}
                  <div className="w-full">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {plan.features.map((feature, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Check className="text-green-600" size={12} />
                          </div>
                          <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="pt-2">
                    {isCurrent ? (
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-2 px-6 py-3 bg-green-50 text-green-700 rounded-xl font-bold border-2 border-green-200 mb-2">
                          <Check size={20} />
                          {select({ es: 'Plan actual', en: 'Current plan' })}
                        </div>
                        {user?.subscription_status === 'cancelled' && (
                          <p className="text-xs text-orange-600">
                            {select({ es: 'Cancelado - activo hasta', en: 'Cancelled - active until' })}<br />
                            {user.subscription_expires_at && formatDate(user.subscription_expires_at)}
                          </p>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => plan.id === 'free' ? handleUpgrade('monthly') : handleUpgrade(plan.id as 'monthly' | 'yearly')}
                        disabled={loading}
                        className={`w-full px-8 py-4 rounded-xl font-bold transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2 ${
                          plan.popular
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg hover:shadow-xl hover:scale-105 hover:from-purple-700 hover:to-pink-700'
                            : plan.id === 'free'
                            ? 'border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                            : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg hover:shadow-xl hover:scale-105 hover:from-yellow-600 hover:to-orange-600'
                        }`}
                      >
                        {loading ? (
                          <>
                            <Loader className="animate-spin" size={18} />
                            <span>{select({ es: 'Procesando...', en: 'Processing...' })}</span>
                          </>
                        ) : (
                          <>
                            {plan.id === 'free' ? (
                              <>
                                <Star size={18} />
                                <span>{select({ es: 'Cambiar', en: 'Switch' })}</span>
                              </>
                            ) : (
                              <>
                                <CreditCard size={18} />
                                <span>
                                  {user?.user_type === 'artist' 
                                    ? select({ es: 'Comenzar como Artista', en: 'Start as Artist' })
                                    : select({ es: 'Suscribirse como Usuario', en: 'Subscribe as User' })
                                  }
                                </span>
                              </>
                            )}
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Integration Notice */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
            <ExternalLink className="text-white" size={24} />
          </div>
          <div>
            <h4 className="text-lg font-bold text-blue-900 mb-2">
              💳 {select({ es: 'Pagos procesados con Stripe', en: 'Payments processed with Stripe' })}
            </h4>
            <p className="text-blue-700 text-sm">
              {select({
                es: 'Las suscripciones y compras se procesan de forma segura a través de Stripe en entorno real.',
                en: 'Subscriptions and purchases are processed securely through Stripe in live environment.',
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="text-red-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{select({ es: '¿Cancelar suscripción?', en: 'Cancel subscription?' })}</h3>
                <p className="text-gray-600">
                  {select({ es: 'Mantendrás acceso premium hasta', en: 'You will keep premium access until' })}{' '}
                  <span className="font-semibold">
                    {user?.subscription_expires_at && formatDate(user.subscription_expires_at)}
                  </span>
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                <h4 className="font-bold text-yellow-900 mb-3">{select({ es: 'Perderás acceso a:', en: 'You will lose access to:' })}</h4>
                <div className="grid grid-cols-2 gap-2 text-yellow-800 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-yellow-600 rounded-full"></div>
                    {select({ es: 'Obras ilimitadas', en: 'Unlimited uploads' })}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-yellow-600 rounded-full"></div>
                    {select({ es: 'Analíticas avanzadas', en: 'Advanced analytics' })}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-yellow-600 rounded-full"></div>
                    {select({ es: 'Insignia premium', en: 'Premium badge' })}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-yellow-600 rounded-full"></div>
                    {select({ es: 'Soporte prioritario', en: 'Priority support' })}
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setShowCancelModal(false)}
                  disabled={loading}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium"
                >
                  {select({ es: 'Mantener plan', en: 'Keep plan' })}
                </button>
                <button
                  onClick={handleCancelSubscription}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all font-medium"
                >
                  {loading ? select({ es: 'Cancelando...', en: 'Cancelling...' }) : select({ es: 'Cancelar', en: 'Cancel' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionTab;
